import React, { useEffect, useMemo, useState } from "react";
import { Banknote, Check, Minus, Plus } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { addManualTransaction } from "@/lib/api";
import { categoryColor } from "@/lib/format";

const INCOME_CATEGORIES = ["Entrate Lavoro", "Altre Entrate"];
const ACCENT = "#00FF87";

const todayStr = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const fieldClass =
  "h-11 w-full rounded-[8px] border border-white/[0.12] bg-[#050508] px-3 text-[13px] font-light text-white placeholder:text-white/25 focus:border-[#00FF87]/45 focus:outline-none focus:ring-1 focus:ring-[#00FF87]/25";

const defaultCategoryFor = (type) => (type === "income" ? "Altre Entrate" : "Spesa Alimentare");

export const CashEntryModal = ({ open, onClose, categories = [], onAdded }) => {
  const [type, setType] = useState("expense");
  const [amount, setAmount] = useState("");
  const [description, setDescription] = useState("");
  const [date, setDate] = useState(todayStr());
  const [category, setCategory] = useState("Spesa Alimentare");
  const [saving, setSaving] = useState(false);

  // reset quando si riapre
  useEffect(() => {
    if (open) {
      setType("expense");
      setAmount("");
      setDescription("");
      setDate(todayStr());
      setCategory("Spesa Alimentare");
      setSaving(false);
    }
  }, [open]);

  const options = useMemo(() => {
    const all = categories.length ? categories : [];
    return type === "income"
      ? all.filter((c) => INCOME_CATEGORIES.includes(c))
      : all.filter((c) => !INCOME_CATEGORIES.includes(c));
  }, [categories, type]);

  const switchType = (next) => {
    setType(next);
    setCategory(defaultCategoryFor(next));
  };

  const isIncome = type === "income";

  const save = async () => {
    const value = parseFloat(String(amount).replace(",", "."));
    if (!Number.isFinite(value) || value <= 0) return toast.error("Inserisci un importo valido");
    if (!description.trim()) return toast.error("Inserisci una descrizione");
    setSaving(true);
    try {
      const tx = await addManualTransaction({
        type,
        amount: value,
        description: description.trim(),
        date,
        category,
      });
      toast.success(isIncome ? "Entrata in contanti aggiunta" : "Spesa in contanti aggiunta");
      onAdded?.(tx);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="max-w-[380px] rounded-[12px] border-white/[0.08] bg-[#0D0D14] p-0 text-white"
        data-testid="cash-entry-modal"
      >
        <DialogHeader className="px-5 pb-0 pt-5 text-left">
          <DialogTitle className="flex items-center gap-2 text-sm font-normal leading-6 text-white/90">
            <Banknote size={16} className="text-[#00FF87]" />
            Movimento in contanti
          </DialogTitle>
          <DialogDescription className="caps-tag pt-1 !text-white/35">
            Soldi reali non tracciati dalla banca · incidono sul saldo
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 px-5 pb-5 pt-4">
          {/* tipo: spesa / entrata */}
          <div className="grid grid-cols-2 gap-2" role="group" aria-label="Tipo movimento">
            <button
              type="button"
              onClick={() => switchType("expense")}
              data-testid="cash-type-expense"
              aria-pressed={!isIncome}
              className={`mono flex h-10 items-center justify-center gap-1.5 rounded-[8px] border text-[11px] font-medium uppercase tracking-[0.12em] transition-colors duration-150 ${
                !isIncome
                  ? "border-white/35 bg-white/[0.06] text-white"
                  : "border-white/[0.1] text-white/40 hover:text-white/70"
              }`}
            >
              <Minus size={13} /> Spesa
            </button>
            <button
              type="button"
              onClick={() => switchType("income")}
              data-testid="cash-type-income"
              aria-pressed={isIncome}
              className={`mono flex h-10 items-center justify-center gap-1.5 rounded-[8px] border text-[11px] font-medium uppercase tracking-[0.12em] transition-colors duration-150 ${
                isIncome
                  ? "border-[#00FF87]/45 bg-[#00FF87]/[0.08] text-[#00FF87]"
                  : "border-white/[0.1] text-white/40 hover:text-white/70"
              }`}
            >
              <Plus size={13} /> Entrata
            </button>
          </div>

          {/* importo */}
          <div className="relative">
            <span
              className={`mono pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[15px] ${
                isIncome ? "text-[#00FF87]" : "text-white/55"
              }`}
            >
              {isIncome ? "+" : "−"}
            </span>
            <input
              autoFocus
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="0,00"
              inputMode="decimal"
              data-testid="cash-amount-input"
              className={`${fieldClass} pl-7 pr-9 text-[15px] tabular`}
            />
            <span className="mono pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-white/35">
              €
            </span>
          </div>

          {/* descrizione */}
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={isIncome ? "Es. Lezione privata" : "Es. Pranzo, mercato…"}
            maxLength={120}
            data-testid="cash-description-input"
            className={fieldClass}
          />

          {/* data + categoria */}
          <div className="flex gap-2">
            <input
              type="date"
              value={date}
              max={todayStr()}
              onChange={(e) => setDate(e.target.value)}
              data-testid="cash-date-input"
              className={`${fieldClass} flex-1 [color-scheme:dark]`}
            />
          </div>

          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger
              className="h-11 w-full rounded-[8px] border-white/[0.12] bg-[#050508] text-[13px] font-light text-white focus:ring-[#00FF87]/25"
              data-testid="cash-category-select"
            >
              <div className="flex items-center gap-2">
                <span className="h-1.5 w-1.5" style={{ background: categoryColor(category) }} />
                <SelectValue placeholder="Categoria" />
              </div>
            </SelectTrigger>
            <SelectContent className="max-h-64 rounded-[8px] border-white/[0.08] bg-[#10101A] text-white">
              {options.map((c) => (
                <SelectItem key={c} value={c} data-testid={`cash-cat-${c.toLowerCase().replace(/\s+/g, "-")}`}>
                  <span className="flex items-center gap-2 font-light">
                    <span className="h-1.5 w-1.5" style={{ background: categoryColor(c) }} />
                    {c}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <button
            onClick={save}
            disabled={saving}
            data-testid="cash-save-button"
            className="mono mt-1 flex h-11 w-full items-center justify-center gap-2 rounded-[8px] border text-[11px] font-medium uppercase tracking-[0.18em] transition-colors duration-150 active:scale-[0.98] disabled:opacity-50"
            style={{
              borderColor: `${ACCENT}66`,
              color: ACCENT,
              background: "rgba(0,255,135,0.06)",
            }}
          >
            <Check size={14} />
            {saving ? "Salvataggio…" : "Aggiungi movimento"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
